import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilterPeriod,
  getSelectedReconciliationDetailsRequest,
  getSelectedReconciliationItemDetailsRequest,
  removeReconciliationItemDetails,
  clearSelectedReconciliation,
  getReconciliationInfoRequest,
} from "../../../redux/actions/reconciliation";
import Button from "../../../components/Button";
import DataTable from "../../../components/DataTable";
import { Input } from "../../../components/Inputs";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import Checkbox from "../../../components/Checkbox/Checkbox";
import moment from "moment";
import { API_EXPORT } from "../../../helpers/api";
import { setNotification } from "../../../helpers/notification";
import fileDownload from "js-file-download";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";

const Reconciliation = ({ t, history }) => {
  const dispatch = useDispatch();
  const { description } = useSelector((state) => state.reconciliation.info);
  const { organizationId } = useSelector(
    (state) => state.authorization.user.organization
  );
  const filterPeriod = useSelector(
    (state) => state.reconciliation.filterPeriod
  );
  const { status } = useSelector((state) => state.reconciliation.info);
  const {
    loadingDetails,
    details,
    loadingItemDetails,
    itemDetails,
    subTotalsInPage,
  } = useSelector((state) => state.reconciliation.selectedReconciliation);

  const [filteredPeriod, setFilteredPeriod] = useState(null);
  const [typeList, setTypeList] = useState([]);
  const [detailsConfig, setDetailsConfig] = useState([]);
  const [
    reconciliationDetailsExportLoading,
    setReconciliationDetailsExportLoading,
  ] = useState(false);
  const [
    reconciliationItemDetailsExportLoading,
    setReconciliationItemDetailsExportLoading,
  ] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({ term: filterPeriod });

  const currentTerm = moment().format("YYYYMM");

  const serverTimeInitial = useSelector(
    (state) => state.utilities.serverTimeInitial
  );
  const serverDate = moment(serverTimeInitial).format("DD.MM.YYYY-HH:mm:ss");

  const splitDate = serverDate.split("-");

  const splitDateArray = splitDate[0].split(".");
  const currentDate = [splitDateArray[2], splitDateArray[1]].join("-");

  const detailConstants = [
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "ALIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "SATIS",
      title: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "PIU",
      title: "PIU",
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "GZ",
      title: t("pages.default.reconciliation.table.fee"),
    },
    {
      totalCredit: 0,
      totalDebit: 0,
      type: "DK",
      title: "Düzeltme Kalemi",
    },
  ];
  const typeTitles = {
    ALIS: "YEK-G " + t("pages.default.reconciliation.table.buy"),
    SATIS: "YEK-G " + t("pages.default.reconciliation.table.sell"),
    PIU: "PIU " + t("pages.default.reconciliation.table.totalSum"),
    PIU_KATILIM: t(
      "pages.default.reconciliation.reconciliationPIUParticipation"
    ),
    GZ: t("pages.default.reconciliation.table.fee"),
    DK: t("pages.default.reconciliation.correctionItem"),
  };
  const statuses = {
    INVOICE: "INVOICE",
  };

  const reconciliationDetailsColumns = [
    {
      name: t("pages.default.reconciliation.table.choose"),
      selector: "choose",
      sortable: false,
      width: "81px",
      cell: (row, index) => (
        <div style={{ display: "flex", justifyContent: "start" }}>
          <Checkbox
            checked={handleCheckboxIsChecked(row)}
            onChange={() => handleCheckBoxChange(row)}
            disabled={
              (!row.totalCredit && !row.totalDebit) || loadingItemDetails
            }
            id={`rc-detail-checkbox-${index}`}
          />
        </div>
      ),
    },
    {
      name: t("pages.default.reconciliation.table.itemName"),
      selector: "type",
      sortable: true,
      cell: (row) => typeTitles[row.type],
      width: "419px",
    },
    {
      name: t("pages.default.reconciliation.table.credit"),
      selector: "totalCredit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-green-300">
          {convertNumberToFrontend({ num: row.totalCredit, digit: 2 })}
        </div>
      ),
      width: "160px",
      style: { textAlign: "left", paddingleft: "25px" },
      headerStyle: { textAlign: "left", paddingRight: "21px" },
    },
    {
      name: t("pages.default.reconciliation.table.debit"),
      selector: "totalDebit",
      sortable: true,
      cell: (row) => (
        <div className="km__u--text-red-300">
          {convertNumberToFrontend({ num: row.totalDebit, digit: 2 })}
        </div>
      ),
      width: "140px",
      style: { textAlign: "left", paddingRight: "25px" },
      headerStyle: { textAlign: "left", paddingRight: "21px" },
    },
    {},
  ];

  const reconciliationItemDetailsColumns = [
    {
      name: t("pages.default.reconciliation.table.processType"),
      selector: "type",
      sortable: true,
      cell: (row) => typeTitles[row.type],
    },
    {
      name: t("pages.default.reconciliation.table.processDay"),
      selector: "createdDate",
      sortable: true,
      cell: (row) => moment(row.createdDate).format("DD.MM.YYYY"),
    },
    {
      name: t("pages.default.reconciliation.table.amount"),
      selector: "amount",
      sortable: true,
      cell: (row) => convertAmountToFrontend(row.amount),
    },
    {
      name: t("pages.default.reconciliation.table.price"),
      selector: "unitPrice",
      sortable: true,
      cell: (row) => convertNumberToFrontend({ num: row.unitPrice, digit: 2 }),
    },
    {
      name: t("pages.default.reconciliation.table.total"),
      selector: "totalPrice",
      sortable: true,
      cell: (row) => convertNumberToFrontend({ num: row.totalPrice, digit: 2 }),
    },
  ];

  useEffect(() => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    dispatch(
      getSelectedReconciliationDetailsRequest({
        organizationId,
        term: filterPeriod,
        operation: "RECONCILIATION",
      })
    );
    setFilteredPeriod(filterPeriod);
    return () => {
      dispatch(clearSelectedReconciliation());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // set Period options
    let d = new Date();
    let filterPeriods = [];
    for (let i = 0; i <= 12; i++) {
      const name =
        t(`ui.default.months.${moment().subtract(i, "month").format("M")}`) +
        " " +
        moment().subtract(i, "month").format("YYYY");
      filterPeriods.push({
        name,
        value: moment().subtract(i, "month").format("YYYYMM"),
      });
      d.setMonth(d.getMonth() - 1);
    }
  }, [t, dispatch]);

  useEffect(() => {
    handleCreateReconciliationDetailsTable();
    if (Array.isArray(details) && details?.length > 0) {
      const typeList = details?.map((detail) => ({
        type: detail.type,
        isActive: false,
      }));
      setTypeList(typeList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const handlePeriodChange = (e) => {
    const listDate = e.target.value.split("-");
    const value = listDate.join("");
    dispatch(setFilterPeriod(value));
  };

  const filterReconciliationList = () => {
    dispatch(getReconciliationInfoRequest(filterPeriod));
    dispatch(
      getSelectedReconciliationDetailsRequest({
        organizationId,
        term: filterPeriod,
        operation: "RECONCILIATION",
      })
    );
    setDownloadFilter({
      term: filterPeriod,
    });
    setFilteredPeriod(filterPeriod);
    itemDetails.length !== 0 && dispatch(clearSelectedReconciliation());
    typeList.length !== 0 && setTypeList([]);
  };

  const handleCreateReconciliationDetailsTable = () => {
    let updatedDetails = [];
    for (let data of detailConstants) {
      if (!(data.type === typeTitles.DK && status !== statuses.INVOICE)) {
        const foundDetail =
          details?.length > 0 &&
          details?.find((detail) => data.type === detail.type);
        if (foundDetail) {
          updatedDetails.push({
            ...foundDetail,
          });
        } else {
          updatedDetails.push({
            ...data,
            totalCredit: 0,
            totalDebit: 0,
          });
        }
      }
    }
    setDetailsConfig(updatedDetails);
  };

  const handleCheckBoxChange = (row) => {
    const updatedTypeList = [...typeList];
    const foundType = updatedTypeList.find((item) => item.type === row.type);
    if (foundType.isActive) {
      dispatch(removeReconciliationItemDetails(foundType.type));
    } else {
      dispatch(
        getSelectedReconciliationItemDetailsRequest(organizationId, {
          term: filterPeriod,
          type: foundType.type,
          operation: "RECONCILIATION",
        })
      );
    }
    foundType.isActive = !foundType.isActive;
    setTypeList(updatedTypeList);
  };

  const handleCheckboxIsChecked = (row) => {
    if (typeList.length !== 0) {
      const foundType =
        typeList.length > 0 && typeList?.find((item) => item.type === row.type);
      return foundType ? foundType.isActive : false;
    }
    return false;
  };

  const handleGoToInvoicingScreen = () => {
    history.push("/invoicing");
  };

  const downloadReconciliationDetails = async () => {
    setReconciliationDetailsExportLoading(true);
    try {
      const res = await API_EXPORT("reconciliation/export/excel", {
        ...downloadFilter,
        organizationId,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setReconciliationDetailsExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setReconciliationDetailsExportLoading(false);
    }
  };

  const downloadReconciliationItemDetails = async () => {
    setReconciliationItemDetailsExportLoading(true);
    const filteredTypeList = typeList.filter((type) => type.isActive);
    const updatedTypeList =
      filteredTypeList.length > 0
        ? filteredTypeList.map((item) => item.type)
        : typeList.map((item) => item.type);
    try {
      const res = await API_EXPORT("reconciliation/export/excel", {
        ...downloadFilter,
        organizationId,
        type: updatedTypeList,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setReconciliationItemDetailsExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setReconciliationDetailsExportLoading(false);
    }
  };

  return (
    <>
      {status === statuses.INVOICE && (
        <div className="km__p--reconciliation-msg km__p--reconciliation-success">
          {t("pages.default.reconciliation.reconciliationFinishedMsg")}
        </div>
      )}
      {status === statuses.ERROR && (
        <div className="km__p--reconciliation-msg km__p--reconciliation-error">
          {t("pages.default.reconciliation.errorMsg")} {description}
        </div>
      )}
      <div className="km__p--reconciliation-header">
        <h5 className="km__u--text-has-emphasis">
          {t("pages.default.reconciliation.title")}
        </h5>
        {filteredPeriod !== currentTerm && status !== statuses.INVOICE && (
          <div className="km__u--text_title">
            {t("pages.default.reconciliation.preReconciliation")}
          </div>
        )}
      </div>

      <div className="km__p--card">
        <div className="km__p--reconciliation-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.reconciliation.title")}
          </div>
          <div className="km__p--reconciliation-filters">
            {/* <Select
              options={filterPeriodOptions}
              name="productionPeriod"
              value={filterPeriod}
              onChange={handlePeriodChange}
              id="rc-productionPeriod"
            /> */}
            <Input
              type="month"
              placement="bottom-end"
              name="historyRedemptionsStartDate"
              placeholder={t("pages.default.redemption.filter.startDate")}
              value={filterPeriod}
              max={currentDate}
              onChange={handlePeriodChange}
            />
            <Button
              label={t("ui.default.filter")}
              onClick={filterReconciliationList}
              id="rc-filter"
            />
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadReconciliationDetails}
              disabled={reconciliationDetailsExportLoading}
              id="rc-download"
            />
            <Button
              type="secondary"
              label={t("pages.default.reconciliation.goToInvoicingScreen")}
              onClick={handleGoToInvoicingScreen}
              id="rc-goToInvoicingScreen"
            />
          </div>
        </div>
        {!loadingDetails && (
          <DataTable
            columns={reconciliationDetailsColumns}
            data={detailsConfig}
            rowKey="type"
            subTotals={[
              {
                totalCount: convertNumberToFrontend({
                  num: subTotalsInPage?.totalCredit,
                  digit: 2,
                }),
                countInPage: "",
                sumIndex: 2,
              },
              {
                totalCount: convertNumberToFrontend({
                  num: subTotalsInPage?.totalDebit,
                  digit: 2,
                }),
                countInPage: "",
                sumIndex: 3,
              },
            ]}
          />
        )}
        {loadingDetails && (
          <Loading
            text={t(
              "pages.default.reconciliation.reconciliationDetailsLoading"
            )}
          />
        )}
      </div>

      <div className="km__p--card">
        <div className="km__p--reconciliation-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.reconciliation.itemDetailsTitle")}
          </div>
          <div className="km__p--reconciliation-filters">
            <Button
              type="secondary"
              label={t("ui.default.download")}
              suffixIcon="download"
              onClick={downloadReconciliationItemDetails}
              disabled={reconciliationItemDetailsExportLoading}
              id="rc-detail-download"
            />
          </div>
        </div>

        {!loadingItemDetails && (
          <DataTable
            columns={reconciliationItemDetailsColumns}
            data={itemDetails}
            rowKey="id"
            pagination
            paginationPerPage={5}
          />
        )}
        {itemDetails.length === 0 && !loadingItemDetails && (
          <EmptyState
            icon="file-text"
            text={t(
              "pages.default.reconciliation.reconciliationItemDetailsNotFound"
            )}
          />
        )}
        {loadingItemDetails && (
          <Loading
            text={t(
              "pages.default.reconciliation.reconciliationItemDetailsLoading"
            )}
          />
        )}
      </div>
    </>
  );
};

export default Reconciliation;
